import { useEffect, useMemo, useState } from "react"

import { formatNumber } from "accounting"
import { Col, Flex, Row, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import DashboardCard from "../../../Components/DashboardCard"
import EmptyComponent from "../../../Components/Empty"
import Private from "../../../Components/Private"
import SearchComponent from "../../../Components/Search"
import TableComponent from "../../../Components/Table"
import { RoutesPaths } from "../../../constants/user-routes"
import useDebounce from "../../../hooks/use-debounce"
import Icon from "../../../Icon"
import { formatCurrency } from "../../../Utils/utils"
import {
  resetFilters,
  setFilters as setDisputeFilters
} from "../../DisputesPage/redux/disputes.slice"
import AnalyticsFilters from "../components/AnalyticsFilters"
import { REPEAT_SUMMARY_CELLS } from "../components/RepeatDisputersPanel"
import { sortableTitle } from "../components/sortableTitle"
import SummaryRows from "../components/SummaryRows/SummaryRows"
import { useAnalyticsScope } from "../hooks/useAnalyticsScope"
import { usePageOf } from "../hooks/usePageOf"
import { ANALYTICS_SECTION } from "../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../redux/analytics.slice"

import "../../DashboardPage/styles.less"
import tables from "../tables.module.less"

const { Title, Text } = Typography

const PAGE_SIZE = 20
const DEFAULT_ORDERING = "-count"

// Every repeat disputer, in the same table shape as the card: rank medal for the top three,
// the customer and their email (both Incognito-masked), then the numbers. Money is masked
// the same way it is everywhere else on these pages. The list is paged on the server, so the
// numeric columns sort there (`ordering`); the medal stays with the row's rank by volume,
// which is what it means, so re-sorting does not renumber anyone.
const repeatColumns = (t) => [
  {
    title: t("Customer"),
    dataIndex: "customer_name",
    key: "customer_name",
    render: (_value, r) => (
      <Flex gap={8} align="center" style={{ minWidth: 0 }}>
        {r.sequence_number <= 3 ? (
          <Icon type={`medal${r.sequence_number}`} />
        ) : (
          <Text style={{ display: "inline-block", minWidth: 20, textAlign: "center" }}>
            {r.sequence_number}
          </Text>
        )}
        <Flex vertical style={{ minWidth: 0 }}>
          <Text
            ellipsis={{ tooltip: r.customer_name || r.customer_email }}
            style={{ fontWeight: 500 }}
          >
            <Private>{r.customer_name || r.customer_email}</Private>
          </Text>
          {r.customer_name ? (
            <Text
              type="secondary"
              ellipsis={{ tooltip: r.customer_email }}
              style={{ fontSize: 12 }}
            >
              <Private>{r.customer_email}</Private>
            </Text>
          ) : null}
        </Flex>
      </Flex>
    )
  },
  {
    title: t("Most disputed category"),
    dataIndex: "top_category_label",
    key: "top_category_label",
    width: 220,
    render: (value) => (
      <Text type={value ? undefined : "secondary"} ellipsis={{ tooltip: value ? t(value) : "" }}>
        {value ? t(value) : "-"}
      </Text>
    )
  },
  {
    title: sortableTitle(t("Last dispute"), "last_dispute_at"),
    dataIndex: "last_dispute_at",
    key: "last_dispute_at",
    width: 150,
    sorter: true,
    render: (value) =>
      value ? (
        <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
          {dayjs(value).format("DD MMM YYYY")}
        </Text>
      ) : (
        <Text type="secondary">-</Text>
      )
  },
  {
    title: sortableTitle(t("Disputes"), "count", { align: "right" }),
    dataIndex: "count",
    key: "count",
    width: 120,
    align: "right",
    defaultSortOrder: "descend",
    sorter: true,
    render: (value) => formatNumber(value)
  },
  {
    title: sortableTitle(t("Open now"), "open_count", { align: "right" }),
    dataIndex: "open_count",
    key: "open_count",
    width: 120,
    align: "right",
    sorter: true,
    render: (value) => (value ? formatNumber(value) : <Text type="secondary">-</Text>)
  },
  {
    title: sortableTitle(t("Value ($)"), "amount", { align: "right" }),
    dataIndex: "amount",
    key: "amount",
    width: 160,
    align: "right",
    sorter: true,
    render: (value) => <Private>{formatCurrency(value, true)}</Private>
  },
  {
    // Null win rate (nothing resolved against this customer yet) renders as a dash rather
    // than a misleading 0%, and sorts last in both directions.
    title: sortableTitle(t("Win rate"), "win_rate", { align: "right" }),
    dataIndex: "win_rate",
    key: "win_rate",
    width: 140,
    align: "right",
    sorter: true,
    render: (value, record) =>
      value == null ? (
        <Text type="secondary">-</Text>
      ) : (
        <Text>{`${formatNumber(value, 0, ",")}% (${formatNumber(record.won_count)}/${formatNumber(record.resolved_count)})`}</Text>
      )
  }
]

// The full repeat-disputer list behind the card's "View all". The card shows the top five;
// this is the whole tail, with the same calendar as the analytics page. A row click opens
// /recover/all filtered to that customer, so the disputes behind a name are one click away.
const RepeatDisputersFullPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { getCompare } = useAnalyticsScope()
  const repeat = useSelector(selectSectionData(ANALYTICS_SECTION.REPEAT_DISPUTERS_PAGE))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.REPEAT_DISPUTERS_PAGE))
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const [ordering, setOrdering] = useState(DEFAULT_ORDERING)
  // Name / email search, the same control and placement as the country breakdown: this list
  // is long, and looking up one customer you already have in mind is the common reason to
  // open the page at all. Debounced, since every search is a request now.
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounce(search.trim(), 300)

  // `getCompare` changes with the window and the case type, which make a new query too: it is
  // what sends the table back to page 1 when the filters above move.
  const query = useMemo(
    () => ({ ordering, ...(debouncedSearch ? { search: debouncedSearch } : {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ordering, debouncedSearch, getCompare]
  )
  const [page, setPage] = usePageOf(query)

  useEffect(() => {
    getCompare(ANALYTICS_SECTION.REPEAT_DISPUTERS_PAGE, { ...query, page, page_size: PAGE_SIZE })
  }, [getCompare, query, page])

  const rows = repeat?.results ?? []
  const summary = repeat?.summary

  // The search narrows the table only: the cards above keep reporting the whole period, the
  // same way the country page's cards do.
  const cards = useMemo(
    () => [
      { title: "Repeat customers", value: summary?.customers_count ?? 0, icon: "user_circle" },
      { title: "Their disputes", value: summary?.disputes_count ?? 0, icon: "receipt_check" },
      { title: "Value at stake", value: summary?.amount ?? 0, prefix: "$", icon: "money" },
      {
        title: "Win rate against them",
        value: summary?.win_rate == null ? null : Math.round(summary.win_rate),
        suffix: "%",
        icon: "pulse"
      }
    ],
    [summary]
  )

  const columns = useMemo(() => repeatColumns(t), [t])

  const handleTableChange = (pagination, _filters, sorter) => {
    const { field, order } = (Array.isArray(sorter) ? sorter[0] : sorter) || {}
    setOrdering(order ? `${order === "descend" ? "-" : ""}${field}` : DEFAULT_ORDERING)
    setPage(pagination.current)
  }

  // Row click: the disputes list, filtered to this customer. The Customer filter is one of
  // the list's default chips, so the narrowing is visible and clearable there.
  const openCustomer = (row) => {
    dispatch(resetFilters())
    dispatch(setDisputeFilters({ customer: [row.customer_email] }))
    navigate(RoutesPaths.recover.all)
  }

  useEffect(() => {
    document.title = "Repeat disputers - Chargeback"
    document.getElementById("global-wrap")?.scrollTo(0, 0)
  }, [])

  return (
    <Row gutter={[0, isMobile ? 12 : 32]} className="dashboard-page">
      <Col span={24}>
        <ButtonComponent
          type="text"
          onClick={() => navigate(RoutesPaths.recover.analytics)}
          icon={<Icon type="chevron_left_small" />}
          style={{ marginLeft: -12 }}
        >
          {t("Back to Analytics")}
        </ButtonComponent>
      </Col>

      <Col span={24}>
        <Flex
          justify="space-between"
          align={!isMobile && "flex-start"}
          style={{ flexDirection: isMobile && "column" }}
        >
          <div style={{ paddingBottom: isMobile && 24 }}>
            <Title level={4} style={{ marginBottom: 4 }}>
              {t("Repeat disputers")}
            </Title>
            <Text type="secondary">
              {t("Customers who disputed more than once in this period, ranked by volume.")}
            </Text>
          </div>

          <AnalyticsFilters />
        </Flex>
      </Col>

      <Col span={24}>
        <Row gutter={[24, 24]}>
          {cards.map((card) => (
            <Col key={card.title} xs={24} sm={12} lg={6}>
              <DashboardCard data={card} theme={theme} />
            </Col>
          ))}
        </Row>
      </Col>

      <Col span={24}>
        <Flex vertical gap={16} className="breakdown-table-header">
          <Title level={5} style={{ marginBottom: 0 }}>
            {t("Customer breakdown")}
          </Title>
          <div className="breakdown-search">
            <SearchComponent
              handleSearch={(e) => setSearch(e.target.value)}
              value={search}
              placeholder={t("Search name or email")}
            />
          </div>
        </Flex>

        {/* The platform's Table (Components/Table), not antd's: it stamps `custom-table`,
            which IS the Chargeback Olympics look - the boxed grey header row with 12px
            rounded ends and a separator under every row. */}
        <TableComponent
          className={tables.olympics}
          dataSource={rows}
          columns={columns}
          loading={fetching}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: repeat?.count ?? 0,
            showSizeChanger: false
          }}
          onChange={handleTableChange}
          scroll={{}}
          rowKey="customer_email"
          tableLayout="fixed"
          showHeader={!!rows.length}
          onRow={(record) => ({
            onClick: () => openCustomer(record),
            style: { cursor: "pointer" }
          })}
          summary={() => (
            <SummaryRows
              summary={rows.length ? summary : null}
              comparisonSummary={repeat?.comparison_summary}
              comparisonLabel={repeat?.comparison_period?.label}
              cells={REPEAT_SUMMARY_CELLS}
            />
          )}
          locale={{
            emptyText: search ? (
              <EmptyComponent type="search" style={{ minHeight: 450 }} />
            ) : (
              <EmptyComponent type="no_data_available" style={{ minHeight: 450 }} />
            )
          }}
        />
      </Col>
    </Row>
  )
}

export default RepeatDisputersFullPage
